import assert from 'assert'

import { Optional } from 'utility-types'
import { l } from '@manychat/manyui'

import * as templatesActions from 'apps/templates/actions/templatesActions'
import { ITemplatesState } from 'apps/templates/constants/templatesInterfaces'
import contentMapAdapter from 'apps/templates/models/ContentMap/adapter'
import { alert } from 'common/core'
import { IThunkAction } from 'common/core/interfaces/actions'
import { CmsApi } from 'shared/api/requests/cms'
import { ContentMapApi } from 'shared/api/requests/contentMap'
import { createAsyncAction } from 'shared/lib/redux'
import { isRequestError } from 'utils/api/mcApi/RequestError'
import { navigatePollyfill } from 'utils/router/tools'
import errorTrackingService from 'utils/services/errorTrackingService'

type Result =
  | { is_template_field: boolean }
  | Optional<
      ITemplatesState['draft']['botFieldsSettings'][string],
      'caption' | 'description' | 'override'
    >

const SELECTION_ERROR = l.makeTranslate(
  'Selection cannot be empty, please return to previous step and select some elements',
)

export function getTemplateFields(draft: ITemplatesState['draft']) {
  const { botFields, botFieldsSettings } = draft

  return botFields.map((field) => {
    const settings = botFieldsSettings[field.field_id] || { is_template_field: true }
    const res: Result = {
      ...settings,
      field_id: field.field_id,
    }
    if (res.value) {
      res.value = field.value
    }
    if ('override' in res && !res.override) {
      delete res.caption
      delete res.description
    }
    delete res.override
    return res
  })
}

export const exportProtectionSettings = (draft: ITemplatesState['draft']) => ({
  is_protected: draft.isProtected,
  is_editable: !draft.isProtected ? true : !draft.isLocked,
  is_tracked: !draft.isProtected ? false : draft.isTracked,
})

export const updateTemplate =
  (template_id: string): IThunkAction =>
  async (dispatch, getState) => {
    const state = getState()
    const draft = state.templates.draft
    const holders = Object.keys(draft.selection).filter((selection) => selection !== 'undefined')

    assert(draft.pageId, 'updateTemplate: template pageId is required')
    if (holders.length === 0) {
      return alert(l.getString(SELECTION_ERROR), 'danger')
    }

    try {
      await dispatch(
        templatesActions.updateTemplate({
          template_id,
          page_id: draft.pageId,
          holders,
          title: draft.name,
          template_fields: getTemplateFields(draft),
          flows_locked_status: draft?.flowsLockedStatus,
          ...exportProtectionSettings(draft),
        }),
      )
      alert(l.translate('Your template successfully updated!'), 'success')
      navigatePollyfill({ pathname: `/profile/templates/${template_id}` }, { replace: true })
    } catch (error) {
      if (isRequestError(error) && error.reason === 'business-logic') return

      errorTrackingService.logError(error, {
        scope: 'root',
        section: null,
        fingerprint: 'template_update-non-business-error',
      })
    }
  }

export const createTemplateFromDraft = (): IThunkAction => async (dispatch, getState) => {
  const state = getState()
  const draft = state.templates.draft
  const holders = Object.keys(draft.selection).filter((selection) => selection !== 'undefined')

  assert(draft.name, 'createTemplateFromDraft: template name is required')
  assert(draft.pageId, 'createTemplateFromDraft: template pageId is required')

  if (holders.length === 0) {
    return alert(l.getString(SELECTION_ERROR), 'danger')
  }

  dispatch(
    templatesActions.createTemplate({
      avatar_file_id: draft.avatar.file_id,
      page_id: draft.pageId,
      holders,
      ...exportProtectionSettings(draft),
      flows_locked_status: draft?.flowsLockedStatus,
      title: draft.name,
      template_fields: getTemplateFields(draft),
    }),
  )
}

export const fetchMap = createAsyncAction('fetchMap', async ({ pageId }: { pageId: string }) => {
  const response = await ContentMapApi.fetchItem({ path: { pageId } })

  return contentMapAdapter(response.data.map)
})

export const fetchFolders = createAsyncAction(
  'fetchFolders',
  async ({ pageId }: { pageId: string }) => {
    const response = await CmsApi.getPageFolders({ path: { pageId } })

    return response.data.folders || []
  },
)
